import type { ComputedRef, Ref } from "vue"
import type { EventVault } from "@/models"
import { useUrlSearchParams } from "@vueuse/core"
import { useRouter } from "vue-router"
import { EventVaultStatus, ExperienceType } from "@/models"
import { EventVaultPageMode, WorkaroundType } from "@/types"

const urlParams = useUrlSearchParams<{ noRedirect?: unknown }>()

/**
 * Handles guest routing for the guest layout.
 */
export function useGuestRedirects(eventVault?: Ref<EventVault | undefined>, albumId?: ComputedRef<number | undefined>, pageToken?: string) {
  const router = useRouter()

  const redirectToLogin = () => {
    if (!eventVault?.value?.id)
      return
    return router.push(`/guest/${eventVault.value.id}/login`)
  }

  const redirectToComingSoon = () => {
    if (!eventVault?.value?.id)
      return
    return router.push(`/guest/${eventVault.value.id}/coming-soon`)
  }

  const redirectToInvitation = () => {
    if (!eventVault?.value?.id)
      return
    return pageToken ? router.push(`/guest/${eventVault.value.id}/invitation?accessToken=${pageToken}`) : router.push(`/guest/${eventVault.value.id}/invitation`)
  }

  const redirectToWelcome = () => {
    if (!eventVault?.value?.id)
      return

    if (eventVault.value.eventVaultSettings?.experienceType === ExperienceType.SinglePage) {
      return albumId?.value ? router.push({ path: `/guest/${eventVault.value.id}`, query: { album: albumId?.value } }) : router.push(`/guest/${eventVault.value.id}`)
    }
    else {
      return albumId?.value ? router.push({ path: `/guest/${eventVault.value.id}/welcome`, query: { album: albumId?.value } }) : router.push(`/guest/${eventVault.value.id}/welcome`)
    }
  }

  const redirectToAccess = () => {
    if (!eventVault?.value?.id)
      return
    return albumId?.value ? router.push({ path: `/guest/${eventVault.value.id}/access/${pageToken}`, query: { album: albumId?.value } }) : router.push(`/guest/${eventVault.value.id}/access/${pageToken}`)
  }

  const redirectTo403 = () => {
    return router.push("/403")
  }

  const handleGuestRouting = () => {
    const currentRoute = router.currentRoute.value
    const isLoginPage = currentRoute.name.toLowerCase() === "/guest/[id]/login"

    // Guard: for login page, don't make routing decisions until eventVault is loaded
    // This prevents incorrect redirects like /guest/undefined/login
    // Other pages (like access) may need to redirect based on other conditions
    if (isLoginPage && !eventVault?.value?.id)
      return

    const hasGuestAccess = eventVault?.value?.isGuest || eventVault?.value?.isHost
    const isEventExperienceSinglePage = eventVault?.value?.eventVaultSettings?.experienceType === ExperienceType.SinglePage

    const workaround = eventVault?.value?.workaround
    const noRedirect = Boolean(urlParams.noRedirect) // url param to prevent redirect

    const eventVaultStatus = eventVault?.value?.status
    const eventVaultSettings = eventVault?.value?.eventVaultSettings

    // `invitationEnabled !== false` (rather than `=== true`) keeps events where the
    // setting has never been explicitly set (undefined) treated as invitation-enabled,
    // while still honouring an explicit host opt-out even if `pageMode` hasn't caught up.
    const isInvitationMode = eventVault?.value?.pageMode === EventVaultPageMode.Invitation && eventVaultSettings?.invitationEnabled !== false && !albumId?.value // page mode is never == invitation if in a context of an album
    const isComingSoonMode = eventVaultStatus === EventVaultStatus.Draft && !isInvitationMode

    const isAccessPage = currentRoute.name.toLowerCase() === "/guest/[id]/access.[token]"
    const isComingSoonPage = currentRoute.name.toLowerCase() === "/guest/[id]/coming-soon"
    const isInvitationPage = currentRoute.name.toLowerCase() === "/guest/[id]/invitation"

    // archived events
    if (eventVaultStatus === EventVaultStatus.Archived) {
      redirectTo403()
      window.location.href = "https://guestpix.com/"
      return
    }

    // routing workarounds
    if (workaround?.redirectUrl && !hasGuestAccess) {
      if (workaround.type === WorkaroundType.Marketing || !isAccessPage) {
        window.location.href = workaround.redirectUrl
        return
      }
    }

    // coming soon routing
    if (isComingSoonMode && !isComingSoonPage) {
      redirectToComingSoon()
    }

    if (eventVaultStatus !== EventVaultStatus.Draft && isComingSoonPage) {
      redirectToLogin()
    }

    // invitation routing
    if (isInvitationMode && !isInvitationPage) {
      const ableToLogin = noRedirect && (isAccessPage || isLoginPage)

      if (eventVaultStatus === EventVaultStatus.Draft) {
        (pageToken || hasGuestAccess) ? redirectToInvitation() : redirectToComingSoon()
      }

      else if (!hasGuestAccess && !ableToLogin) {
        pageToken ? redirectToInvitation() : redirectToLogin()
      }
    }

    if (!isInvitationMode && isInvitationPage) {
      if (!eventVaultSettings?.invitationEnabled) {
        if (hasGuestAccess) {
          redirectToWelcome()
        }
        else {
          pageToken ? redirectToAccess() : redirectToLogin()
        }
      }
      return
    }

    // already authenticated guests/hosts
    if (hasGuestAccess && (isLoginPage || isAccessPage)) {
      redirectToWelcome()
    }

    // cookie requirement gate
    if (!hasGuestAccess && currentRoute?.meta?.requiresCookies) {
      redirectToLogin()
    }

    // page layout mismatch
    if ((isEventExperienceSinglePage && currentRoute?.meta?.pageLayout === "multi") || (!isEventExperienceSinglePage && currentRoute?.meta?.pageLayout === "single")) {
      redirectToWelcome()
    }
  }

  return {
    handleGuestRouting,
  }
}
